import { guestApiClient } from './apiClient';
import { ServiceRequest, ApiResponse } from '../types';

/** A one-tap request suggestion; the labels are i18n keys, not copy. */
export interface QuickService {
  id: string;
  nameKey: string;
  descriptionKey: string;
}

export interface CreateServiceRequestData {
  type: 'housekeeping' | 'maintenance' | 'concierge' | 'other';
  description: string;
  priority: 'low' | 'medium' | 'high';
  requested_time?: string;
}

class ServiceRequestService {
  /**
   * Create a new service request
   */
  async createServiceRequest(requestData: CreateServiceRequestData): Promise<ApiResponse<ServiceRequest>> {
    try {
      // Omit empty requested_time — the backend DateTimeField rejects "" with a 400
      const payload: CreateServiceRequestData = { ...requestData };
      if (!payload.requested_time) {
        delete payload.requested_time;
      }
      const response = await guestApiClient.post<ServiceRequest>('/api/guest/service-requests/', payload);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get all service requests for the current guest
   */
  async getMyServiceRequests(): Promise<ApiResponse<ServiceRequest[]>> {
    try {
      const response = await guestApiClient.get<ServiceRequest[]>('/api/guest/service-requests/');
      
      // Handle potential nested response format from guest API
      const requestsData = response.data;
      if (!requestsData) {
        console.warn('getMyServiceRequests: API returned null data');
        return {
          success: true,
          data: []
        };
      }
      
      // If data is already an array, return as-is
      if (Array.isArray(requestsData)) {
        return response;
      }
      
      // Handle paginated response format (e.g., {count: 3, results: [...]})
      if (typeof requestsData === 'object' && requestsData !== null && 'results' in requestsData && Array.isArray((requestsData as any).results)) {
        return {
          success: true,
          data: (requestsData as any).results
        };
      }
      
      // If data is nested (e.g., {data: [...]}), extract the array
      if (typeof requestsData === 'object' && requestsData !== null && 'data' in requestsData && Array.isArray((requestsData as any).data)) {
        return {
          success: true,
          data: (requestsData as any).data
        };
      }
      
      console.warn('getMyServiceRequests: Unexpected data format, returning empty array:', requestsData);
      return {
        success: true,
        data: []
      };
    } catch (error: any) {
      console.error('getMyServiceRequests: API error:', error);
      throw error;
    }
  }

  /**
   * Get a specific service request
   */
  async getServiceRequest(requestId: string): Promise<ApiResponse<ServiceRequest>> {
    try {
      const response = await guestApiClient.get<ServiceRequest>(`/api/guest/service-requests/${requestId}/`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update a service request (only if still pending)
   */
  async updateServiceRequest(requestId: string, updates: Partial<CreateServiceRequestData>): Promise<ApiResponse<ServiceRequest>> {
    try {
      const response = await guestApiClient.patch<ServiceRequest>(`/api/guest/service-requests/${requestId}/`, updates);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Cancel a service request
   */
  async cancelServiceRequest(requestId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await guestApiClient.patch<{ message: string }>(`/api/guest/service-requests/${requestId}/cancel/`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get service request status updates
   */
  async getServiceRequestStatus(requestId: string): Promise<ApiResponse<{ status: string; updated_at: string }>> {
    try {
      const response = await guestApiClient.get<{ status: string; updated_at: string }>(`/api/guest/service-requests/${requestId}/status/`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get available service types and their descriptions
   */
  async getServiceTypes(): Promise<ApiResponse<Array<{ type: string; name: string; description: string; icon: string }>>> {
    try {
      const response = await guestApiClient.get<Array<{ type: string; name: string; description: string; icon: string }>>('/api/guest/service-types/');
      return response;
    } catch (error: any) {
      // Fallback to default service types if API not available
      return {
        success: true,
        data: [
          {
            type: 'housekeeping',
            name: 'Housekeeping',
            description: 'Room cleaning, fresh towels, amenities',
            icon: 'home'
          },
          {
            type: 'maintenance',
            name: 'Maintenance',
            description: 'Room issues, repairs, technical problems',
            icon: 'wrench'
          },
          {
            type: 'concierge',
            name: 'Concierge',
            description: 'Local recommendations, bookings, assistance',
            icon: 'bell'
          },
          {
            type: 'other',
            name: 'Other',
            description: 'General requests and inquiries',
            icon: 'help'
          }
        ]
      };
    }
  }

  /**
   * Common housekeeping requests offered as one-tap suggestions. Copy
   * lives in the locale bundles (services.options.housekeeping.*) so the
   * list follows the guest's language like the rest of the form.
   */
  getHousekeepingServices(): QuickService[] {
    return [
      'extraTowels',
      'roomCleaning',
      'amenitiesRefill',
      'laundry',
      'extraPillows',
    ].map((id) => ({
      id,
      nameKey: `services.options.housekeeping.${id}.name`,
      descriptionKey: `services.options.housekeeping.${id}.description`,
    }));
  }
}

export const serviceRequestService = new ServiceRequestService();